import ProjectOverviewDonutChart from "@components/charts/ProjectOverviewDonutChart";
import Input from "@components/common/Input";
import ListTable from "@components/common/ListTable";
import EditIcon from "@components/icons/EditIcon";
import FloppyDiskIcon from "@components/icons/FloppyDiskIcon";
import InfoIcon from "@components/icons/InfoIcon";
import PlusIcon from "@components/icons/PlusIcon";
import ModalAlert from "@components/Modals/ModalAlert";
import {
	createScapExtensionNorway,
	updateScapExtensionNorway,
} from "@services/creditLimitManagement/scapService";
import { fetchDatascapdistr, fetchvesselItemwithredirect } from "@services/vesselService";
import { IS_DISTRIBUTOR_ROLE } from "@utils/user_roles";
import { Tooltip } from "flowbite-react";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

const useFetchVessels = (searchTerms, token, dispatch, navigate, roleId, vessel_id) => {
	const [vessels, setVessels] = useState([]); // State to store vessel data
	const [loading, setLoading] = useState(false);

	const fetchVesselsData = useCallback(
		debounce(async () => {
			if (!token) return;

			setLoading(true);
			const distributor_role = roleId === IS_DISTRIBUTOR_ROLE ? true : false;
			try {
				const response = await fetchDatascapdistr(
					dispatch,
					token,
					navigate,
					searchTerms,
					distributor_role,
					vessel_id,
				);

				if (response) {
					setVessels(response); // Store the API response
				}
			} catch (error) {
				console.error("Error fetching vessels:", error);
			} finally {
				setLoading(false);
			}
		}, 300),
		[searchTerms, token, dispatch, navigate],
	);

	useEffect(() => {
		fetchVesselsData();
		return () => fetchVesselsData.cancel();
	}, [fetchVesselsData]);

	return { vessels, loading };
};
const ProjectOverviewContent = ({ vesselData, vessel_id }) => {
	const { id } = useParams();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { token, role, clients, distributor } = useSelector((state) => state.auth.user);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);
	const [successMessage, setSuccessMessage] = useState(false);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [searchTerms, setSearchTerms] = useState({
		filter: vesselData?.client?.id,
		distributor_id: "",
	});

	const { vessels, loading } = useFetchVessels(
		searchTerms,
		token,
		dispatch,
		navigate,
		role?.id,
		vessel_id,
	);

	const [newExtension, setNewExtension] = useState([]);

	const [errors, setErrors] = useState({
		extensionValue: "",
		extensionCurrentValue: "",
	});

	const columns = [
		{ header: "Site Name", field: "vessel_name", sortable: true },
		{
			header: "Kit Numbers",
			content: (row) =>
				row.starlinks.length > 0 ? (
					<div>
						{row.starlinks.map((starlink, index) => (
							<div key={index} className="mb-1">
								{starlink.kit_number}
							</div>
						))}
					</div>
				) : (
					"No Kits"
				),
		},
		{
			header: "Usage (GB)",
			content: (row) =>
				row.starlinks.length > 0 ? (
					<div>
						{row.starlinks.map((starlink, index) => (
							<div key={index} className="mb-1">
								{starlink.usage_tb}
							</div>
						))}
					</div>
				) : (
					"No Usage"
				),
		},
		{
			header: "Total Usage (GB)",

			content: (row) => (
				<div className="font-bold text-base text-gray-200">{row.total_usage_tb}</div>
			),
		},
	];
	const columnsDistributor = [
		{ header: "Client", field: "client_name", sortable: true },
		{
			header: "Usage (GB)",

			content: (row) => <div className="font-bold text-base text-gray-200">{row.usage_gb}</div>,
		},
	];

	// useEffect(() => {

	//   if (vesselData?.client?.id != null) {
	//     setSearchTerms((prevTerms) => ({
	//       ...prevTerms,
	//       filter: vesselData?.client?.id,
	//     }));
	//   }

	//   const existingExtensions =
	//     vesselData?.starlinks?.flatMap((starlink) =>
	//       starlink.scap_configs?.flatMap(
	//         (config) =>
	//           config.extensions?.map((ext) => ({
	//             id: ext.id,
	//             extensionValue: ext.limit_value?.toString() || "",
	//             extensionCurrentValue: ext.limit_current_value?.toString() || "",
	//           })) || []
	//       )
	//     ) || [];

	//   setNewExtension(existingExtensions);

	// }, [role, clients, vesselData]);

	useEffect(() => {
		if (vesselData?.client?.id != null) {
			setSearchTerms((prevTerms) => ({
				...prevTerms,
				filter: vesselData?.client?.id,
			}));
		}

		const allExtensions =
			vesselData?.starlinks?.flatMap((starlink) =>
				starlink.scap_configs?.flatMap(
					(config) =>
						config.extensions?.map((ext) => ({
							id: ext.id,
							extensionValue: ext.limit_value?.toString() || "",
							extensionCurrentValue: ext.limit_current_value?.toString() || "",
						})) || [],
				),
			) || [];

		const uniqueExtensions = Object.values(
			allExtensions.reduce((acc, ext) => {
				if (!acc[ext.id]) {
					acc[ext.id] = ext; // keep the first occurrence
				}
				return acc;
			}, {}),
		);

		setNewExtension(uniqueExtensions);
	}, [role, clients, vesselData]);

	const handleAddExtensionClick = () => {
		setNewExtension((prev) => [
			...prev,
			{
				extensionValue: "",
				extensionCurrentValue: null,
			},
		]);
	};

	const handleDeleteExtensionClick = (indexToRemove) => {
		setNewExtension((prev) => prev.filter((_, index) => index !== indexToRemove));
	};

	const handleFieldExtensionChange = (index, field, value, isNew = false) => {
		if (isNew) {
			const updated = [...newExtension];
			updated[index][field] = value;
			setNewExtension(updated);
		}
	};

	const validateForm = () => {
		const newErrors = {};
		const isValid = true;

		newExtension.forEach((ext, extIndex) => {
			if (!ext.extensionValue) {
				newErrors[`extensionValue_${extIndex}`] =
					"Extension is required — or remove this extension section if not needed.";
			}
			if (ext.extensionValue < 1) {
				newErrors[`extensionValue_${extIndex}`] = "Extension value must be at least 1.";
			}

			if (ext.extensionValue > 100000000) {
				newErrors[`extensionValue_${extIndex}`] =
					"Extension value must not be greater than 99999999.99.";
			}
		});

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};
	const scapVessels = vesselData.starlinks.filter((vessel) => vessel.is_scap);

	let scapId = "";
	let scap_Configs = [];
	// Get their scap_configs arrays
	scapVessels.forEach((vessel) => {
		scap_Configs = vessel.scap_configs;
		scapId = vessel.scap_configs[0].id;
	});

	// Submit handler
	const handleEditScap = async (id) => {
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// Find the extension that matches the clicked one
			const ext = newExtension.find((e) => e.id === id);

			if (!ext) {
				console.error("Extension not found for id:", id);
				setErrorMessage(`Extension not found for id: ${id}`);
				setShowErrorModal(true);
				return;
			}

			// Skip if both fields are empty
			const hasLimitValues = ext.extensionValue?.trim() || ext.extensionCurrentValue?.trim();

			if (!hasLimitValues) {
				console.warn("No values to submit for extension:", id);
				return;
			}

			const scapData = {
				id: ext.id,
				limit_value: ext.extensionValue,
				limit_current_value: ext.extensionCurrentValue,
			};

			const updatedScapExtension = await updateScapExtensionNorway(
				scapId, // SCAP id
				scapData, // payload
				id, // idExt (the one being clicked)
				token,
				dispatch,
				navigate,
			);

			if (updatedScapExtension && updatedScapExtension.errors) {
				const errorMessages = [];

				Object.keys(updatedScapExtension.errors).forEach((errorKey) => {
					updatedScapExtension.errors[errorKey].forEach((errorMessage) => {
						errorMessages.push(`${errorKey}: ${errorMessage}`);
					});
				});

				if (errorMessages.length > 0) {
					setErrorMessage(errorMessages.join("\n"));
					setShowErrorModal(true);
					return;
				}
			} else if (!updatedScapExtension) {
				console.error("Failed to submit Scap extension.");
				setErrorMessage(`Failed to submit Scap extension ${scapId}`);
				setShowErrorModal(true);
				return;
			}

			setShowSuccessModal(true);
			setTimeout(async () => {
				const url = `/vessel/${vessel_id}`;
				await fetchvesselItemwithredirect(vessel_id, "", token, dispatch, navigate, url);
			}, 1000);
		} catch (error) {
			console.error("Error during Scap extension update:", error);
			setErrorMessage(`Error during Scap extension update`);
			setShowErrorModal(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCreateScap = async (extIndex) => {
		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			const ext = newExtension[extIndex]; // get the clicked row

			if (!ext || !ext.extensionValue) {
				console.error("Extension value is required");
				setErrorMessage("Please provide an Extension Value before submitting.");
				setShowErrorModal(true);
				return;
			}

			const scapData = {
				limit_value: ext.extensionValue,
			};

			const createdScapExtension = await createScapExtensionNorway(
				scapId, // SCAP id
				scapData,
				token,
				dispatch,
				navigate,
			);

			if (createdScapExtension && createdScapExtension.errors) {
				const errorMessages = [];

				Object.keys(createdScapExtension.errors).forEach((errorKey) => {
					createdScapExtension.errors[errorKey].forEach((errorMessage) => {
						errorMessages.push(`${errorKey}: ${errorMessage}`);
					});
				});

				if (errorMessages.length > 0) {
					setErrorMessage(errorMessages.join("\n"));
					setShowErrorModal(true);
					return;
				}
			} else if (!createdScapExtension) {
				console.error("Failed to create Scap extension.");
				setErrorMessage(`Failed to create Scap extension`);
				setShowErrorModal(true);
				return;
			}

			setShowSuccessModal(true);
			setTimeout(async () => {
				const url = `/vessel/${vessel_id}`;
				await fetchvesselItemwithredirect(vessel_id, "", token, dispatch, navigate, url);
			}, 1000);
		} catch (error) {
			console.error("Error during Scap extension create:", error);
			setErrorMessage(`Error during Scap extension create`);
			setShowErrorModal(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="px-4 pt-2 pb-4">
			<div className="custom-scrollbar relative mt-0 overflow-x-auto bg-navbar-bg sm:rounded-lg">
				<div className="mt-0 text-white">
					{/* <h2 className="text-lg font-bold">Total Usage</h2> */}
					{loading ? (
						<p className="text-gray-400">Loading ...</p>
					) : vessels != "" ? (
						<ul className="m-0">
							<div className="mb-2">
								<ProjectOverviewDonutChart
									id={`credit_limit-0`} // Unique ID for credit limit char
									allowanceValue={vessels?.global_plan_name}
									allowanceCurrentValue={vessels?.total_saipem_usage_tb}
									paddingValue={false}
									allowanceExtensionValue={vessels?.global_extensions_total}
								/>
							</div>

							{/* extention scap value */}

							<fieldset className="grid gap-2 border-dark-blue border-t border-b pt-4 pb-4">
								<div className="flex items-start justify-between gap-2">
									<span className="flex items-start justify-start font-semibold text-white text-xs">
										Manage Limit Value Extension
									</span>
									<div className="block">
										{/* add button */}
										<div className="flex h-full items-end justify-end">
											<button
												type="button"
												className="!bg-button-bg hover:!bg-opacity-70 mx-1 inline-flex h-8 w-8 items-center justify-center rounded-lg text-white transition-colors duration-200 hover:text-dark-blue focus:outline-none focus:ring-2 focus:ring-blue-gray"
												onClick={() => handleAddExtensionClick()}
											>
												<Tooltip
													animation="duration-150"
													content="Add New Extension"
													className="!bg-dark-blue bg-opacity-80 text-xs"
												>
													<PlusIcon className="h-4 w-4" />
												</Tooltip>
											</button>
										</div>
									</div>
								</div>

								<div className="flex items-start gap-2">
									<InfoIcon className="text-blue-gray" />
									<span className="contents font-normal text-blue-gray text-xs sm:text-sm">
										Add multiple extensions to extend the credit limit.
									</span>
								</div>
								<div className="custom-scrollbar max-h-[300px] w-full max-w-auto overflow-auto pt-1 pb-1">
									{/* Dynamically added extension with delete buttons */}
									{newExtension.map((ext, extIndex) => (
										<div key={extIndex} className="relative w-full">
											{/* Absolute Delete Button */}
											<div className="flex justify-end"></div>
											<div className="flex gap-4">
												<div className="flex-1 border-slate-800 border-t px-3 pt-3 pb-3">
													{(ext?.extensionValue === null || !ext.id) && (
														<button
															type="button"
															className="absolute top-1 right-0 flex h-6 w-6 items-center justify-center rounded-lg bg-transparent text-gray-400 text-sm hover:bg-dropdown-hover hover:text-white"
															onClick={() => handleDeleteExtensionClick(extIndex)}
														>
															<svg
																className="h-3 w-3"
																xmlns="http://www.w3.org/2000/svg"
																fill="none"
																viewBox="0 0 14 14"
															>
																<path
																	stroke="currentColor"
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth="2"
																	d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
																/>
															</svg>
														</button>
													)}
													<div
														className={`grid ${id ? "p-0 lg:grid-cols-3" : "lg:grid-cols-2"} gap-2`}
													>
														{" "}
														<div className="grid w-full gap-2">
															<Input
																id="extensionValueid"
																type="number"
																placeholder="Extension Value"
																label="Extension Value *"
																value={ext.extensionValue}
																onChange={(value) =>
																	handleFieldExtensionChange(
																		extIndex,
																		"extensionValue",
																		value,
																		true,
																	)
																}
																onClear={() =>
																	handleFieldExtensionChange(extIndex, "extensionValue", "", true)
																}
																className={`button-bg block h-9 w-9 rounded-lg border border-gray-600 py-3 text-gray-400 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 ${
																	errors[`extensionValue_${extIndex}`] ? "border-red-500" : ""
																}`}
																required
															/>
															{errors[`extensionValue_${extIndex}`] && (
																<p className="text-red-500 text-xs">
																	{errors[`extensionValue_${extIndex}`]}
																</p>
															)}
														</div>
														<div className="grid gap-2">
															<Input
																id="extensionCurrentValueid"
																placeholder="Extension Current Value"
																label="Extension Current Value "
																value={ext.extensionCurrentValue}
																className={`button-bg } block h-9 w-9 cursor-not-allowed rounded-lg border border-gray-600 py-3 text-blue-gray text-sm placeholder-gray-400 opacity-60" focus:border-primary-500 focus:ring-primary-500`}
																isDisabled={true}
															/>
														</div>
														<div className="flex items-center justify-start gap-2">
															{ext.extensionValue !== null && ext.id && (
																<button
																	key={extIndex}
																	type="button"
																	className="hover:!bg-opacity-70 h-10 w-10 rounded-lg pt-3 text-blue-gray transition-colors duration-200 hover:text-slate-300 focus:outline-none"
																	onClick={() => handleEditScap(ext.id)}
																>
																	<Tooltip
																		animation="duration-150"
																		content="Edit Extension"
																		className="!bg-dark-blue bg-opacity-80 text-xs"
																	>
																		<EditIcon className="h-6 w-6 md:h-10 md:w-10 lg:h-10 lg:w-10" />
																	</Tooltip>
																</button>
															)}
															{(ext?.extensionValue === null || !ext.id) && (
																<button
																	key={extIndex}
																	type="button"
																	className="hover:!bg-opacity-70 relative h-10 w-10 rounded-lg pt-3 text-blue-gray transition-colors duration-200 hover:text-slate-300 focus:outline-none"
																	onClick={() => handleCreateScap(extIndex)}
																>
																	<Tooltip
																		animation="duration-150"
																		content="Add Extension"
																		className="!bg-dark-blue bg-opacity-80 text-xs"
																	>
																		<FloppyDiskIcon className="h-6 w-6 md:h-10 md:w-10 lg:h-10 lg:w-10" />
																	</Tooltip>
																</button>
															)}
														</div>
													</div>
												</div>
											</div>
										</div>
									))}
								</div>
							</fieldset>

							<ListTable columns={columns} list={vessels.vessels} isAction={false} />
						</ul>
					) : (
						<p>No projects found</p>
					)}
				</div>
			</div>

			<ModalAlert
				isOpen={showErrorModal}
				type="error"
				closeModal={() => setShowErrorModal(false)}
				message={errorMessage}
			/>

			{/* Success Modal */}
			<ModalAlert
				isOpen={showSuccessModal}
				type="success"
				closeModal={() => setShowSuccessModal(false)}
				message={successMessage}
			/>
		</div>
	);
};

export default ProjectOverviewContent;
